import React from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAppConfig } from "@/hooks/useAppConfig";
import { X } from "lucide-react";
import "./InfoPanelExpanded.css";

const InfoPanelExpanded: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { getPageConfig, appName } = useAppConfig();

  // Get the page route from URL params, fallback to 'home'
  const currentPageRoute = searchParams.get('page') || 'home';
  
  // Get page-specific configuration
  const pageConfig = getPageConfig(currentPageRoute);

  // Get all info sections from config, fallback to defaults
  const infoSections = pageConfig?.infoList || [
    {
      title: "Data Sources",
      list: [
        {
          header: "ACES PCdb",
          context: "The ultimate parts library. If it's got a part, we know it.",
        },
        {
          header: "Predii Taxonomy",
          context: "Handpicked by auto experts, so you're not just getting data - you're getting wisdom on wheels.",
        },
        {
          header: "VMRS",
          context: "The maintenance playbook that keeps fleets running. Standard codes that make sense of every repair.",
        },
      ],
    },
  ];

  const handleClose = () => {
    navigate(-1); // Go back to previous page
  };

  return (
    <div className="info-panel-expanded">
      {/* Advanced Background Effects */}
      <div className="bg-grid"></div>
      <div className="bg-glow-primary"></div>
      <div className="bg-glow-secondary"></div>
      <div className="particle-field">
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
        <div className="particle"></div>
      </div>
      
      {/* Close Button */}
      <button 
        className="info-panel-expanded__close"
        onClick={handleClose}
        aria-label="Close expanded view"
      >
        <X size={20} />
      </button>

      {/* Main Content Container */}
      <div className="content-container">
        {/* Hero Section */}
        <div className="hero-section">
          <div className="hero-content">
            <div className="logo-container">
              <div className="logo-glow"></div>
              <img src="/plain_logo.png" alt="Predii Logo" className="page-logo-image" />
            </div>
            
            <div className="text-content">
              <h1 className="main-title">
                <span className="title-gradient">{appName}</span>
              </h1>
              
              <div className="subtitle-container">
                <span className="subtitle-badge">
                  {pageConfig?.agentLabel || "Parts Agent"}
                </span>
              </div>
              
              <div className="description-section">
                <h2 className="description-title">
                  {pageConfig?.infoHeader || "Your AI parts counter assistant powered by Predii Agent."}
                </h2>
                <p className="description-text">
                  {pageConfig?.infoSubHeader || "Finds parts the way you ask for them — understands shop talk, fixes typos, and knows that \"brake shoes\" and \"brake pads\" aren't always the same thing."}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Info Sections */}
        <div className="info-sections">
          {infoSections.map((section, sectionIndex) => (
            <div key={sectionIndex} className="info-section">
              <h3 className="section-title">{section.title}</h3>
              <div className="cards-grid">
                {section.list.map((item, index) => (
                  <div key={index} className="info-card">
                    <div className="card-glow"></div>
                    <div className="card-content">
                      <h4 className="card-title">{item.header}</h4>
                      <p className="card-description">{item.context}</p>
                    </div>
                    <div className="card-accent"></div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default InfoPanelExpanded;